import { Component, FormEvent } from 'react';
import * as _ from 'lodash';
import * as moment from 'moment';
import { connect, Dispatch } from 'react-redux';
import { AppActionCreators, FollowUpReportsActionCreators, IApplicationState } from "@nkr/store/index";
import { IFollowUpDonorForList } from '@nkr/models/follow-up';
import { ReportTableWrapper } from '../../styles';
import { IFollowUpReconciliationReportResponse } from '@nkr/store/donor';
import { FormControl, Glyphicon, Well } from 'react-bootstrap';

interface IProps {
    centerId: number,
    isAdmin: boolean,
    isAdminLogin: boolean
}

interface IStateProps {
}

interface IDispatchProps {
    showLoading: (message: string) => Promise<void>,
    hideLoading: () => void,
    getReconciliationReport: (centerId: number) => Promise<IFollowUpReconciliationReportResponse>,
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    data: IFollowUpDonorForList[],
    sortBy: string,
    sortDirection: string,
    centerFilter: number
}

class FollowUpReconciliationReport extends Component<TProps, IState> {
    readonly state: IState = {
        data: [],
        sortBy: 'donationDate',
        sortDirection: 'desc',
        centerFilter: 0
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        this.props.showLoading('Getting ready, one moment please...').then(() => {
            this.props.getReconciliationReport(this.props.centerId).then(response => {
                if (response.data.success) {
                    this.setState({
                        data: response.data.results
                    });
                }

                this.props.hideLoading();
            })
        })
    }

    getCenters = () => {
        let list = this.state.data.map(x => {
            return { id: x.centerId, alias: x.centerAlias };
        });

        let centers = _.uniqBy(list, x => x.id);

        return _.sortBy(centers, item => item.alias);
    }

    getFilteredData = () => {
        return this.state.data.filter(x => {
            let centerFilter = this.state.centerFilter > 0 ? x.centerId == this.state.centerFilter : true;
            return centerFilter;
        })
    }

    handleSort = (property: string) => {
        let { sortBy, sortDirection } = this.state;

        if (sortBy == property) {
            if (sortDirection == 'asc') {
                sortDirection = 'desc'
            } else {
                if (sortDirection == 'desc') {
                    sortBy = '';
                }
            }
        } else {
            sortBy = property;
            sortDirection = 'asc';
        }

        this.setState({
            sortBy,
            sortDirection
        });
    }

    handleCenterChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            centerFilter: parseInt(e.currentTarget.value)
        });
    }

    renderHeaderSort = (property: string) => {
        if (this.state.sortBy == property) {
            return <Glyphicon glyph={this.state.sortDirection == 'asc' ? 'triangle-top' : 'triangle-bottom'} style={{ marginLeft: 5 }} />;
        } else {
            return null;
        }
    }

    render = () => {
        const centerList = this.getCenters();

        const data = _.orderBy(this.getFilteredData(), [item => {
            switch (this.state.sortBy) {
                case 'firstName': return item.firstName;
                case 'lastName': return item.lastName;
                case 'gender': return item.gender;
                case 'birthDate': return moment(item.birthDate, 'MM/DD/YYYY');
                case 'city': return item.city;
                case 'state': return item.state;
                case 'donationDate': return moment(item.donationDate, 'MM/DD/YYYY');
            }
        }], [this.state.sortDirection]);

        return (
            <div className={this.props.isAdminLogin ? 'container' : null}>
                <div className='alert alert-info'>This page lists donors that have been added to follow-up but were not added through DASH. This can help to reconcile your DASH statistics with your actual donations.</div>

                {this.props.isAdmin && (
                    <Well bsSize='sm'>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div style={{ marginRight: 5 }}>Center:</div>
                            <FormControl
                                componentClass='select'
                                value={this.state.centerFilter}
                                onChange={this.handleCenterChange}
                            >
                                <option value={0}>- All Centers -</option>
                                {centerList.map(center =>
                                    <option key={'ctr-' + center.id} value={center.id}>{center.alias}</option>
                                )}
                            </FormControl>
                        </div>
                    </Well>
                )}

                <ReportTableWrapper condensed striped>
                    <thead>
                        <tr>
                            <th>#</th>

                            {this.props.isAdmin && (
                                <th>Center</th>
                            )}

                            <th onClick={() => this.handleSort('firstName')}>First Name {this.renderHeaderSort('firstName')}</th>
                            <th onClick={() => this.handleSort('lastName')}>Last Name {this.renderHeaderSort('lastName')}</th>
                            <th onClick={() => this.handleSort('gender')}>Gender {this.renderHeaderSort('gender')}</th>
                            <th onClick={() => this.handleSort('birthDate')}>Birth Date {this.renderHeaderSort('birthDate')}</th>
                            <th onClick={() => this.handleSort('city')}>City {this.renderHeaderSort('city')}</th>
                            <th onClick={() => this.handleSort('state')}>State {this.renderHeaderSort('state')}</th>
                            <th onClick={() => this.handleSort('donationDate')}>Donated {this.renderHeaderSort('donationDate')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((item, index) =>
                            <tr key={'reconciliation-' + index}>
                                <td>{index + 1}</td>

                                {this.props.isAdmin && (
                                    <td>{item.centerAlias}</td>
                                )}

                                <td>{item.firstName}</td>
                                <td>{item.lastName}</td>
                                <td>{item.gender}</td>
                                <td>{item.birthDate}</td>
                                <td>{item.city}</td>
                                <td>{item.state}</td>
                                <td>{item.donationDate}</td>
                            </tr>
                        )}
                    </tbody>
                </ReportTableWrapper>
            </div>
        );
    }
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        app: state.app
    };
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            return dispatch(AppActionCreators.hideLoading());
        },
        getReconciliationReport: (centerId) => {
            return dispatch(FollowUpReportsActionCreators.getReconciliationReport(centerId));
        },
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(FollowUpReconciliationReport);